import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/AppShell';
import { sessionQueryOptions } from '@/features/auth/queries';
import { LoginPage } from '@/pages/LoginPage';
import { AccountPage } from '@/pages/AccountPage';
import { PropertiesPage } from '@/pages/PropertiesPage';
import { NewPropertyPage } from '@/pages/NewPropertyPage';
import { PropertyDetailPage } from '@/pages/PropertyDetailPage';
import { EditPropertyPage } from '@/pages/EditPropertyPage';
import { NewLoanPage } from '@/pages/NewLoanPage';
import { LoanDetailPage } from '@/pages/LoanDetailPage';
import { EditLoanPage } from '@/pages/EditLoanPage';
import { SimulateurHypothesesPage } from '@/pages/SimulateurHypothesesPage';
import { SimulateurMarchePage } from '@/pages/SimulateurMarchePage';
import { SimulateurPage } from '@/pages/SimulateurPage';
import { SimulateurAchatReventePage } from '@/pages/SimulateurAchatReventePage';
import { SimulateurSecteursPage } from '@/pages/SimulateurSecteursPage';
import { SimulateurProjectionPage } from '@/pages/SimulateurProjectionPage';
import { AssignmentQueuePage } from '@/pages/AssignmentQueuePage';
import { AssignmentRulesPage } from '@/pages/AssignmentRulesPage';
import { StatementsPage } from '@/pages/StatementsPage';
import { TransactionsPage } from '@/pages/TransactionsPage';
import { PatrimoinePage } from '@/pages/PatrimoinePage';
import { BilanPage } from '@/pages/BilanPage';
import { PrevisionPage } from '@/pages/PrevisionPage';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => <p className="p-8 text-muted-foreground">Page introuvable.</p>,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context }) => {
    // Déjà connecté : inutile de revoir l'écran de connexion.
    const hasSession = await context.queryClient
      .ensureQueryData(sessionQueryOptions)
      .then(() => true)
      .catch(() => false);
    if (hasSession) throw redirect({ to: '/biens' });
  },
  component: LoginPage,
});

/** Disposition pathless : protège tout ce qui est en dessous et fournit le cadre visuel. */
const authenticatedRoute = createRoute({
  id: '_authenticated',
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(sessionQueryOptions);
    } catch {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/biens' });
  },
});

const accountRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/compte',
  component: AccountPage,
});

const propertiesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens',
  component: PropertiesPage,
});

const newPropertyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens/nouveau',
  component: NewPropertyPage,
});

const propertyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens/$propertyId',
  component: PropertyDetailPage,
});

const editPropertyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens/$propertyId/modifier',
  component: EditPropertyPage,
});

const newLoanRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens/$propertyId/prets/nouveau',
  component: NewLoanPage,
});

const loanRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens/$propertyId/prets/$loanId',
  component: LoanDetailPage,
});

const editLoanRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/biens/$propertyId/prets/$loanId/modifier',
  component: EditLoanPage,
});

const simulateurRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/simulateur',
  component: SimulateurPage,
});

const simulateurProjectionRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/simulateur/projection',
  component: SimulateurProjectionPage,
});

const simulateurMarcheRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/simulateur/marche',
  component: SimulateurMarchePage,
});

const simulateurSecteursRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/simulateur/secteurs',
  component: SimulateurSecteursPage,
});

const simulateurAchatReventeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/simulateur/achat-revente',
  component: SimulateurAchatReventePage,
});

const simulateurHypothesesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/simulateur/hypotheses',
  // Reprise d'une valeur mesurée (calibrage DVF) : un décimal, jamais enregistré sans validation.
  validateSearch: (search: Record<string, unknown>): { housingPriceGrowth?: number } => {
    const value = Number(search['housingPriceGrowth']);
    return search['housingPriceGrowth'] !== undefined && Number.isFinite(value) ? { housingPriceGrowth: value } : {};
  },
  component: SimulateurHypothesesPage,
});

const assignmentQueueRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/affectation',
  component: AssignmentQueuePage,
});

const assignmentRulesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/affectation/regles',
  component: AssignmentRulesPage,
});

const statementsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/affectation/releves',
  component: StatementsPage,
});

const transactionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/ledger',
  component: TransactionsPage,
});

const patrimoineRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/ledger/patrimoine',
  component: PatrimoinePage,
});

const bilanRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/ledger/bilan',
  component: BilanPage,
});

const previsionRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/ledger/prevision',
  component: PrevisionPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    indexRoute,
    accountRoute,
    propertiesRoute,
    newPropertyRoute,
    propertyRoute,
    editPropertyRoute,
    newLoanRoute,
    loanRoute,
    editLoanRoute,
    simulateurRoute,
    simulateurProjectionRoute,
    simulateurMarcheRoute,
    simulateurSecteursRoute,
    simulateurAchatReventeRoute,
    simulateurHypothesesRoute,
    assignmentQueueRoute,
    assignmentRulesRoute,
    statementsRoute,
    transactionsRoute,
    patrimoineRoute,
    bilanRoute,
    previsionRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
